/* ClinicPatientCardPreview — doctor-side preview of what patients will
 * download from the Patient Portal → My Profile → ID card tab.
 *
 * iter98.50 — Surfaces the clinic-branded member card as a static
 * preview inside Doctor Profile → Branding accordion. Doctors get to
 * see exactly what their patients see (logo, clinic name, address),
 * which doubles as a confidence-builder during onboarding ("look,
 * your patients can download this with YOUR branding").
 *
 * Uses placeholder patient data (sample name "Riya Patel" + sample
 * code "INV-PT-XXXXXX") so the doctor sees the LAYOUT, not real PII.
 * Same visual styling as PatientIdCard.jsx (pinned 440×275 px, fully
 * inline styles, no Tailwind for the rendered area).
 *
 * Props:
 *   clinicName, clinicLogo (data URL), clinicAddress, clinicPhone — read
 *   from the current Doctor Profile form so changes preview live as
 *   the doctor edits.
 */
import React from "react";
import { QRCodeSVG } from "qrcode.react";


function clinicInitials(name) {
    if (!name) return "C";
    const words = String(name).replace(/[,.()]/g, " ").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "C";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

const SAMPLE_NAME = "Riya Patel";
const SAMPLE_CODE = "INV-PT-XXXXXX";
const SAMPLE_INITIALS = "RP";

// iter100.68 — Format an ISO date to "MMM YYYY" for the member-since chip.
// Returns null when the input is falsy or invalid, so callers can fall
// back to the current month (which is what a brand-new doctor would see
// on their first login).
function formatMemberSince(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}


export default function ClinicPatientCardPreview({
    clinicName,
    clinicLogo,
    clinicAddress,
    clinicPhone,
    clinicEmail,
    memberSince,     // iter100.68 — ISO date string of the doctor's created_at.
    showHeader = true, // iter100.68 — Parent accordion now owns the section
                       // heading (Member Card Preview + LIVE badge), so we
                       // let the parent suppress our internal header.
}) {
    // Build a sample vCard so the QR even renders correctly during
    // preview (same format the patient card uses).
    const safe = (s) => (s || "").replace(/[;,\\\n\r]/g, " ").trim();
    const vcardLines = ["BEGIN:VCARD", "VERSION:3.0"];
    if (clinicName) {
        vcardLines.push(`FN:${safe(clinicName)}`);
        vcardLines.push(`ORG:${safe(clinicName)}`);
    } else {
        vcardLines.push("FN:Your Clinic");
    }
    if (clinicPhone)   vcardLines.push(`TEL;TYPE=WORK,VOICE:${safe(clinicPhone)}`);
    if (clinicEmail)   vcardLines.push(`EMAIL;TYPE=WORK:${safe(clinicEmail)}`);
    if (clinicAddress) vcardLines.push(`ADR;TYPE=WORK:;;${safe(clinicAddress)};;;;`);
    vcardLines.push("END:VCARD");
    const qrPayload = vcardLines.join("\n");

    // iter100.68 — Dynamic "Member since <MMM YYYY>" from the doctor's
    // created_at. Falls back to the current month for brand-new doctors
    // whose created_at hasn't been round-tripped yet.
    const memberSinceLabel = formatMemberSince(memberSince)
        || new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" });

    const S = {
        card: {
            width: 440, height: 275, position: "relative", overflow: "hidden",
            borderRadius: 16, color: "#ffffff",
            background: "linear-gradient(135deg, hsl(193 100% 12%) 0%, hsl(192 100% 20%) 45%, hsl(173 60% 30%) 100%)",
            boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35)",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        },
        blob1: { position: "absolute", top: -60, right: -60, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" },
        blob2: { position: "absolute", bottom: -70, left: "40%", width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" },
        body: { position: "relative", height: "100%", padding: 20, display: "flex", flexDirection: "column", boxSizing: "border-box" },
        clinicRow: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "0 0 auto" },
        clinicLogoImg: { width: 36, height: 36, borderRadius: 6, objectFit: "contain", background: "#ffffff", padding: 2, flexShrink: 0, boxSizing: "border-box" },
        clinicInitialsBox: { width: 36, height: 36, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#ffffff", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.20)", boxSizing: "border-box", flexShrink: 0 },
        clinicName: { fontSize: 15, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        clinicCardLabel: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.22em", color: "rgba(255,255,255,0.65)", fontWeight: 600, marginTop: 2 },
        identityRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flex: "1 1 auto" },
        identityLeft: { display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 auto" },
        patientInitialsBox: { width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.12)", border: "2px solid rgba(255,255,255,0.40)", display: "flex", alignItems: "center", justifyContent: "center", color: "#ffffff", fontSize: 16, fontWeight: 700, flexShrink: 0, boxSizing: "border-box" },
        patientText: { minWidth: 0, flex: "1 1 auto" },
        patientName: { fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2, color: "#ffffff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        patientCode: { fontSize: 10, fontFamily: "ui-monospace, 'SF Mono', 'Roboto Mono', monospace", letterSpacing: "0.08em", color: "rgba(255,255,255,0.85)", marginTop: 4 },
        qrWrap: { background: "#ffffff", borderRadius: 6, padding: 5, flexShrink: 0, lineHeight: 0 },
        footerRow: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, color: "rgba(255,255,255,0.75)", flex: "0 0 auto" },
        memberSince: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 },
        clinicAddr: { fontSize: 9, color: "rgba(255,255,255,0.70)", marginTop: 2, maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        verifiedPill: { fontSize: 8, textTransform: "uppercase", letterSpacing: "0.10em", color: "rgba(255,255,255,0.70)", fontWeight: 700, display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" },
        poweredBy: { fontSize: 8.5, color: "rgba(255,255,255,0.62)", marginTop: 4, letterSpacing: "0.04em", textAlign: "right" },
    };

    return (
        <div data-testid="clinic-card-preview-wrap">
            {showHeader && (
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                        <div className="text-xs font-semibold">Patient member card preview</div>
                        <div className="text-[11px] text-muted-foreground">
                            This is exactly what your patients see when they download their card. Updates as you edit clinic details above.
                        </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                        Live preview
                    </span>
                </div>
            )}

            <div style={S.card} data-testid="clinic-card-preview">
                <div style={S.blob1} aria-hidden="true" />
                <div style={S.blob2} aria-hidden="true" />
                <div style={S.body}>
                    {/* Clinic brand row */}
                    <div style={S.clinicRow}>
                        {clinicLogo ? (
                            <img src={clinicLogo} alt={clinicName || "Clinic"} style={S.clinicLogoImg} />
                        ) : (
                            <div style={S.clinicInitialsBox}>{clinicInitials(clinicName)}</div>
                        )}
                        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                            <div style={S.clinicName}>{clinicName || "Your Clinic Name"}</div>
                            <div style={S.clinicCardLabel}>Patient Member Card</div>
                        </div>
                    </div>

                    {/* Patient identity row */}
                    <div style={S.identityRow}>
                        <div style={S.identityLeft}>
                            <div style={S.patientInitialsBox}>{SAMPLE_INITIALS}</div>
                            <div style={S.patientText}>
                                <div style={S.patientName}>{SAMPLE_NAME}</div>
                                <div style={S.patientCode}>{SAMPLE_CODE}</div>
                            </div>
                        </div>
                        <div style={S.qrWrap}>
                            <QRCodeSVG value={qrPayload} size={64} level="M" bgColor="#ffffff" fgColor="hsl(193 100% 12%)" />
                        </div>
                    </div>

                    {/* Footer row */}
                    <div style={S.footerRow}>
                        <div>
                            <div style={S.memberSince}>📅 Member since {memberSinceLabel}</div>
                            {clinicAddress && <div style={S.clinicAddr}>{clinicAddress}</div>}
                        </div>
                        <div>
                            <div style={S.verifiedPill}>
                                <span style={{ display: "inline-block", width: 8, height: 8, background: "currentColor", clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)" }} aria-hidden="true" />
                                Verified
                            </div>
                            <div style={S.poweredBy}>
                                Powered by Invisique™<sup style={{ fontSize: 6 }}>™</sup>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                Sample patient shown for layout preview. Real patients see their own name, code &amp; photo.
            </p>
        </div>
    );
}
